import { useCallback, useEffect, useState } from 'react';
import { get, post, getErrorMessage } from '../utils/api';
import { getOrCreateDeviceId } from '../utils/deviceId';
import {
  UseSentimentVotingOptions,
  UseSentimentVotingReturn,
  SentimentSnapshot,
  SentimentVoteOption,
  SentimentVoteRequest,
  SentimentVoteSummary,
  SentimentApiEnvelope,
  SentimentApiPayload,
  SENTIMENT_OPTIONS,
  createSeededSentimentSnapshot
} from '../types/sentiment';

interface SentimentState {
  sentiment: SentimentSnapshot;
  isLoading: boolean;
  isVoting: boolean;
  error: string | null;
}

const buildSnapshot = (
  payload?: SentimentApiPayload,
  fallbackVote: SentimentVoteOption | null = null
): SentimentSnapshot => {
  const seed = createSeededSentimentSnapshot();

  const counts: Record<SentimentVoteOption, number> = {
    buy: payload?.buy?.count ?? seed.buy.count,
    hold: payload?.hold?.count ?? seed.hold.count,
    sell: payload?.sell?.count ?? seed.sell.count
  };

  const totalVotes = payload?.total_votes ?? Math.max(counts.buy + counts.hold + counts.sell, 0);

  const getPayloadSummary = (option: SentimentVoteOption): SentimentVoteSummary | undefined => {
    switch (option) {
      case 'buy':
        return payload?.buy;
      case 'hold':
        return payload?.hold;
      case 'sell':
      default:
        return payload?.sell;
    }
  };

  const summaries = SENTIMENT_OPTIONS.reduce<Record<SentimentVoteOption, SentimentVoteSummary>>((acc, option) => {
    const summary = getPayloadSummary(option);
    const percentage = summary?.percentage ?? (totalVotes > 0
      ? Number(((counts[option] / totalVotes) * 100).toFixed(1))
      : 0);

    acc[option] = {
      count: counts[option],
      percentage,
      seeded: summary?.seeded ?? totalVotes <= seed.totalVotes
    };

    return acc;
  }, {
    buy: seed.buy,
    hold: seed.hold,
    sell: seed.sell
  });

  const resolvedVote = payload?.userVote ?? fallbackVote ?? null;

  return {
    buy: summaries.buy,
    hold: summaries.hold,
    sell: summaries.sell,
    totalVotes,
    userVoted: payload?.userVoted ?? !!resolvedVote,
    userVote: resolvedVote,
    refreshedAt: new Date().toISOString()
  };
};

export const useSentimentVoting = (
  options: UseSentimentVotingOptions = {}
): UseSentimentVotingReturn => {
  const { symbol, category, date, enabled = true, isAuthenticated = true } = options;

  const [state, setState] = useState<SentimentState>(() => ({
    sentiment: createSeededSentimentSnapshot(),
    isLoading: false,
    isVoting: false,
    error: null
  }));
  // Reset sentiment when dependencies change
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      sentiment: createSeededSentimentSnapshot(),
      error: null
    }));
  }, [symbol, category, date]);

  const fetchSentiment = useCallback(async () => {
    if (!enabled || !symbol || !category) {
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Pass deviceId for popular category so backend can check anonymous vote status
      const params: Record<string, string | null | undefined> = { category, date };
      if (category === 'popular') {
        params.deviceId = getOrCreateDeviceId();
      }

      const response = await get<SentimentApiEnvelope>(
        `/api/stocks/${encodeURIComponent(symbol)}/sentiment`,
        { params }
      );

      const payload = response.data?.data?.sentiment;
      const snapshot = buildSnapshot(payload);
      setState({ sentiment: snapshot, isLoading: false, isVoting: false, error: null });
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[useSentimentVoting] Failed to fetch sentiment data:', error);
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, [enabled, symbol, category, date]);

  const submitVote = useCallback(async (vote: SentimentVoteOption, investmentCoins?: number) => {
    if (!symbol || !category) {
      setState((prev) => ({ ...prev, error: 'Unable to submit vote: missing stock context.' }));
      return;
    }

    setState((prev) => ({ ...prev, isVoting: true, error: null }));

    try {
      let response;

      if (!isAuthenticated && category === 'popular') {
        // Anonymous vote on popular category
        response = await post<SentimentApiEnvelope>(
          `/api/stocks/${encodeURIComponent(symbol)}/vote/guest`,
          {
            vote,
            category,
            deviceId: getOrCreateDeviceId(),
            date: date ?? undefined
          }
        );
      } else {
        // Authenticated vote
        const payload: SentimentVoteRequest = {
          vote,
          category,
          date: date ?? undefined,
          ...(investmentCoins && investmentCoins > 0 ? { investmentCoins } : {})
        };

        response = await post<SentimentApiEnvelope>(
          `/api/stocks/${encodeURIComponent(symbol)}/vote`,
          payload
        );
      }

      const apiPayload = response.data?.data?.sentiment;
      const snapshot = buildSnapshot(apiPayload, vote);
      setState({ sentiment: snapshot, isLoading: false, isVoting: false, error: null });
    } catch (error) {
      const message = getErrorMessage(error);
      console.error('[useSentimentVoting] Failed to submit vote:', error);
      setState((prev) => ({ ...prev, isVoting: false, error: message }));
      throw error;
    }
  }, [symbol, category, date, isAuthenticated]);

  // Auto-fetch sentiment when ready
  useEffect(() => {
    fetchSentiment();
  }, [fetchSentiment]); // fetchSentiment already includes all necessary dependencies

  const refetch = useCallback(async () => {
    await fetchSentiment();
  }, [fetchSentiment]); // fetchSentiment already includes all necessary dependencies

  const { sentiment, isLoading, isVoting, error } = state;

  return {
    sentiment,
    isLoading,
    isVoting,
    error,
    hasVoted: sentiment.userVoted,
    userVote: sentiment.userVote,
    submitVote,
    refetch
  };
};

export default useSentimentVoting;
